// Landing page: render the live top-10 leaderboard (light theme). Best-effort - if the
// API is unreachable (e.g. opened via `vite dev` with no Worker) it shows an empty state.

import { LeaderboardClient, flagEmoji } from './net/leaderboardClient';
import { avatarCanvas, spriteToCanvas } from './render/pixelArt';
import { ABILITIES, ALTAR, BOSS_TYPES, ENEMY_TYPES, WET } from './game/balance';
import { ABILITY_ICONS, makeIconCanvas } from './ui/icons';

declare global {
  interface Window {
    posthog?: { capture: (event: string, props?: Record<string, unknown>) => void };
  }
}

const RANK_COLOR = ['#c79a13', '#8a8597', '#b06a32']; // gold / silver / bronze

async function renderBoard(): Promise<void> {
  const el = document.getElementById('board');
  if (el === null) return;
  const entries = await new LeaderboardClient().fetchLeaderboard();
  el.replaceChildren();

  if (entries.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'board-empty';
    empty.textContent = 'No scores yet - be the first to make the board!';
    el.appendChild(empty);
    return;
  }

  entries.slice(0, 20).forEach((entry, i) => {
    const row = document.createElement('div');
    row.className = 'board-row';

    const rk = document.createElement('div');
    rk.className = 'rk';
    rk.textContent = `${i + 1}`;
    if (RANK_COLOR[i]) rk.style.color = RANK_COLOR[i];

    const av = avatarCanvas(entry.avatar ?? 0, 2);

    const who = document.createElement('div');
    who.textContent = `${flagEmoji(entry.country)}  ${entry.name}`;

    const sc = document.createElement('div');
    sc.className = 'sc';
    sc.textContent = entry.score.toLocaleString();

    row.append(rk, av, who, sc);
    el.appendChild(row);
  });
}

// Animated alchemist above the title: runs in place (frame swap + bob) and leans/turns
// toward the mouse, so the hero feels alive without any gameplay running.
function heroCharacter(): void {
  const canvas = document.getElementById('heroChar') as HTMLCanvasElement | null;
  if (canvas === null) return;
  const frames = ['playerA', 'playerB'].map((k) => spriteToCanvas(k, 1)); // native pixels
  const CHAR_W = 60; // displayed character width
  const CHAR_H = Math.round((CHAR_W * frames[0].height) / frames[0].width);
  const padX = 22; // room to lean sideways
  const padY = 8; // room to bob
  canvas.width = CHAR_W + padX * 2;
  canvas.height = CHAR_H + padY;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return;
  ctx.imageSmoothingEnabled = false;

  let mouseX = window.innerWidth / 2;
  window.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
  });

  let t = 0;
  const step = (): void => {
    t += 1;
    const f = frames[Math.floor(t / 8) % frames.length]; // ~7.5fps run cadence
    const bob = Math.abs(Math.sin(t * 0.16)) * padY;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const lean = Math.max(-padX, Math.min(padX, (mouseX - cx) / 22));
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(canvas.width / 2 + lean, padY - bob);
    if (mouseX < cx) ctx.scale(-1, 1); // face the cursor
    ctx.drawImage(f, -CHAR_W / 2, 0, CHAR_W, CHAR_H);
    ctx.restore();
    requestAnimationFrame(step);
  };
  step();
}

// Abilities showcase (icon + when you get it + what it does), straight from game data.
function renderAbilities(): void {
  const el = document.getElementById('abilities');
  if (el === null) return;
  el.replaceChildren();
  for (const a of ABILITIES) {
    const card = document.createElement('div');
    card.className = 'info-card';
    const icon = makeIconCanvas(ABILITY_ICONS[a.key] ?? [], a.color, 3);
    icon.className = 'info-icon';
    const name = document.createElement('div');
    name.className = 'info-name';
    name.textContent = a.name;
    const meta = document.createElement('div');
    meta.className = 'info-meta';
    meta.textContent = `Level ${a.unlockLevel} · key ${a.label}`;
    const desc = document.createElement('div');
    desc.className = 'info-desc';
    desc.textContent = a.desc;
    card.append(icon, name, meta, desc);
    el.appendChild(card);
  }
}

// Bestiary teaser - real sprites + HP/damage from game data.
const MONSTERS: ReadonlyArray<{ key: string; name: string; trait: string }> = [
  { key: 'slime', name: 'Slime Blob', trait: 'The endless fodder horde' },
  { key: 'spore', name: 'Spore Swarmer', trait: 'Fast and fragile' },
  { key: 'glass', name: 'Glass Shard', trait: 'Quick, attacks in clusters' },
  { key: 'wraith', name: 'Ink Wraith', trait: 'Weaves to dodge your shots' },
  { key: 'vat', name: 'Bloated Vat', trait: 'Splits into slimes on death' },
  { key: 'mortar', name: 'Mortar Mold', trait: 'Lobs shots from range' },
  { key: 'scorpion', name: 'Acid Scorpion', trait: 'Elite - charges you down' },
];

function renderMonsters(): void {
  const el = document.getElementById('monsters');
  if (el === null) return;
  el.replaceChildren();
  for (const m of MONSTERS) {
    const t = ENEMY_TYPES[m.key];
    if (t === undefined) continue;
    const card = document.createElement('div');
    card.className = 'info-card';
    const sprite = spriteToCanvas(t.sprite, 3);
    sprite.className = 'info-icon';
    const name = document.createElement('div');
    name.className = 'info-name';
    name.textContent = m.name;
    const meta = document.createElement('div');
    meta.className = 'info-meta';
    meta.textContent = `${t.hp} HP · ${t.contactDmg} dmg`;
    const desc = document.createElement('div');
    desc.className = 'info-desc';
    desc.textContent = m.trait;
    card.append(sprite, name, meta, desc);
    el.appendChild(card);
  }
}

// Bosses - set-piece fights, with their real sprites + the moment they arrive.
const BOSSES: ReadonlyArray<{ key: string; name: string; trait: string }> = [
  { key: 'flask', name: 'Overflowing Flask', trait: 'Summons slimes · ~5:00' },
  { key: 'construct', name: 'Quicksilver Construct', trait: 'Fast, splits apart · ~10:00' },
  { key: 'catalyst', name: 'The Catalyst', trait: 'The run-ender · 15:00' },
];

function renderBosses(): void {
  const el = document.getElementById('bosses');
  if (el === null) return;
  el.replaceChildren();
  for (const b of BOSSES) {
    const t = BOSS_TYPES[b.key];
    if (t === undefined) continue;
    const card = document.createElement('div');
    card.className = 'info-card';
    const sprite = spriteToCanvas(t.sprite, 3);
    sprite.className = 'info-icon';
    const name = document.createElement('div');
    name.className = 'info-name';
    name.textContent = b.name;
    const meta = document.createElement('div');
    meta.className = 'info-meta';
    meta.textContent = `${t.hp.toLocaleString()} HP · ${t.contactDmg} dmg`;
    const desc = document.createElement('div');
    desc.className = 'info-desc';
    desc.textContent = b.trait;
    card.append(sprite, name, meta, desc);
    el.appendChild(card);
  }
}

// Arena terrain - sprites + what each thing does (numbers straight from balance data).
const ARENA: ReadonlyArray<{ sprite: string; name: string; desc: string }> = [
  { sprite: 'boulder', name: 'Boulders', desc: 'Half-buried rocks block you, enemies, and projectiles alike - use them as cover.' },
  {
    sprite: 'puddle1',
    name: 'Puddles',
    desc: `Step in and you're soaked: move speed is halved for ${WET.durationSec}s after you leave. Kite fast foes through them.`,
  },
  {
    sprite: 'altar',
    name: 'Healing Altar',
    desc: `Stand on it to heal ${Math.round(ALTAR.healFrac * 100)}% of max HP, then it recharges over ${ALTAR.cooldownSec}s.`,
  },
];

function renderArena(): void {
  const el = document.getElementById('arena');
  if (el === null) return;
  el.replaceChildren();
  for (const a of ARENA) {
    const card = document.createElement('div');
    card.className = 'info-card';
    const sprite = spriteToCanvas(a.sprite, 3);
    sprite.className = 'info-icon';
    const name = document.createElement('div');
    name.className = 'info-name';
    name.textContent = a.name;
    const desc = document.createElement('div');
    desc.className = 'info-desc';
    desc.textContent = a.desc;
    card.append(sprite, name, desc);
    el.appendChild(card);
  }
}

// Count clicks on the "Play the game for free" CTAs (PostHog, landing only).
function trackCtaClicks(): void {
  document.querySelectorAll<HTMLAnchorElement>('a.cta').forEach((el, i) => {
    el.addEventListener('click', () => {
      window.posthog?.capture('play_cta_clicked', { position: i === 0 ? 'hero' : 'footer' });
    });
  });
}

void renderBoard();
heroCharacter();
renderArena();
renderAbilities();
renderMonsters();
renderBosses();
trackCtaClicks();
